import { useState } from "react";
import { ReactLenis } from "lenis/react";
import { motion } from "framer-motion";
import { Clock3, Download, ShieldCheck, Check, MessageCircle, Award, GraduationCap, BookOpen, Languages, Users } from "lucide-react";
import LeadForm from "@/components/landing/LeadForm";
import SuccessCard from "@/components/landing/SuccessCard";
import SiteFooter from "@/components/landing/SiteFooter";
import HowItWorks from "@/components/landing/HowItWorks";
import Faq from "@/components/landing/Faq";
import Reveal from "@/components/landing/Reveal";
import { translations } from "@/lib/i18n";

const CRED_ICONS = [Award, GraduationCap, BookOpen, Languages];
const TRUST_ICONS = [Clock3, Download, ShieldCheck];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Header = ({ t, lang, onToggle }) => (
  <header className="fixed top-0 inset-x-0 z-40 bg-sand/90 backdrop-blur-md border-b border-line" data-testid="site-header">
    <div className="flex items-center justify-between px-5 sm:px-10 lg:px-16 h-16">
      <a href="#inicio" data-testid="brand-link" className="flex items-center">
        <img src="/elba-logo.png" alt="Elba Rojas Psicóloga" className="h-11 w-auto" />
      </a>
      <div className="flex items-center gap-3">
        <button onClick={onToggle} data-testid="language-toggle"
          className="rounded-full border border-line bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:border-sage hover:text-sage">
          {lang === "es" ? "EN" : "ES"}
        </button>
        <a href="#formulario" data-testid="header-cta-button"
          className="rounded-full bg-sage px-5 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-sage-dark">
          {t.headerCta}
        </a>
      </div>
    </div>
  </header>
);

export default function Landing() {
  const [leadName, setLeadName] = useState(null);
  const [lang, setLang] = useState("es");
  const t = translations[lang];
  const toggleLang = () => setLang((l) => (l === "es" ? "en" : "es"));

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true, anchors: true }}>
      <div id="inicio" className="grain-overlay bg-sand text-ink font-sans antialiased selection:bg-clay/40">
        <Header t={t} lang={lang} onToggle={toggleLang} />
        <main>
          <section className="relative overflow-hidden px-5 sm:px-10 lg:px-16 pt-28 sm:pt-36 pb-16 sm:pb-24" data-testid="hero-section">
            <div aria-hidden className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-clay/20 blur-3xl" />
            <div aria-hidden className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-sage/10 blur-3xl" />
            <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-20 items-center max-w-6xl mx-auto">
              <div>
                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="text-xs uppercase tracking-[0.25em] font-bold text-sage mb-7"
                  data-testid="hero-kicker"
                >
                  {t.kicker}
                </motion.p>
                <h1 className="font-serif tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-ink" data-testid="hero-title">
                  <Line delay={0.15}>{t.heroA}</Line>
                  <Line delay={0.3}>{t.heroB} <em className="text-sage">{t.heroAccent}</em></Line>
                </h1>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-7 max-w-lg text-base leading-relaxed tracking-wide text-ink/70"
                >
                  {t.intro1}<span className="font-semibold text-ink">{t.introStrong}</span>{t.intro2}
                  <span className="font-semibold text-ink">{t.introStrong2}</span>{t.intro3}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-9 flex flex-wrap items-center gap-4"
                >
                  <motion.a href="#formulario" data-testid="hero-primary-cta" whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-2 rounded-full bg-sage px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage-dark">
                    <Download className="h-4 w-4" /> {t.ctaPrimary}
                  </motion.a>
                  <motion.a href={t.waUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button" whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#1eb85a]">
                    <MessageCircle className="h-4 w-4" /> {t.ctaWhats}
                  </motion.a>
                </motion.div>
              </div>

              <Reveal delay={0.2}>
                <div className="relative max-w-sm mx-auto">
                  <div aria-hidden className="absolute -inset-3 rounded-t-[10rem] rounded-b-3xl bg-clay/25 rotate-2" />
                  <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-3xl border border-line bg-white p-2.5">
                    <img src="/elba-foto.jpg" alt="Psic. Elba Rojas" data-testid="hero-photo"
                      className="h-[420px] sm:h-[500px] w-full object-cover object-[50%_28%] rounded-t-[9rem] rounded-b-2xl" />
                  </div>
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-white border border-line px-6 py-3 shadow-lg" data-testid="hero-photo-badge">
                    <p className="font-serif text-sm text-ink">Psic. Elba Rojas</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-stonemute">{t.photoBadgeSub}</p>
                  </div>
                </div>
              </Reveal>
            </div>

            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="relative mt-14 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-7 max-w-6xl mx-auto"
              data-testid="hero-credentials"
            >
              {t.creds.map((c, i) => {
                const Icon = CRED_ICONS[i];
                return (
                <li key={c.title} className="flex items-start gap-3">
                  <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full bg-white border border-line">
                    <Icon className="h-4 w-4 text-sage" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-ink leading-snug">{c.title}</span>
                    <span className="block text-xs text-stonemute leading-snug mt-0.5">{c.sub}</span>
                  </span>
                </li>
                );
              })}
            </motion.ul>
            <motion.ul
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="relative mt-9 flex flex-wrap justify-center gap-x-7 gap-y-3 text-xs text-stonemute max-w-6xl mx-auto"
            >
              {t.trust.map((text, i) => {
                const Icon = TRUST_ICONS[i];
                return (
                <li key={text} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-clay" /> {text}
                </li>
                );
              })}
            </motion.ul>
          </section>

          <section className="border-y border-line bg-white px-5 sm:px-10 lg:px-16 py-9" data-testid="guide-strip">
            <div className="max-w-6xl mx-auto flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-10">
              <p className="font-serif text-xl shrink-0">{t.stripGift} <em className="text-sage">{t.stripGiftName}</em></p>
              <ul className="flex flex-wrap gap-x-8 gap-y-2.5 text-sm text-ink/80">
                {t.stripItems.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="h-4 w-4 shrink-0 text-clay" /> {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs uppercase tracking-[0.2em] text-stonemute lg:ml-auto shrink-0">{t.stripNote}</p>
            </div>
          </section>

          <section className="px-5 sm:px-10 lg:px-16 py-12 sm:py-16" data-testid="services-section">
            <div className="max-w-6xl mx-auto">
              <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
                <h2 className="font-serif tracking-tight text-2xl sm:text-3xl">{t.servicesTitle} <em className="text-sage">{t.servicesAccent}</em></h2>
                <p className="text-xs uppercase tracking-[0.2em] text-stonemute">{t.servicesSub}</p>
              </Reveal>
              <Reveal delay={0.08}>
                <ul className="flex flex-wrap gap-3">
                  {t.services.map((s, i) => (
                    <li key={s} data-testid={`service-pill-${i}`}
                      className="rounded-full border border-line bg-white px-5 py-2.5 text-sm text-ink/80 transition-colors duration-300 hover:border-sage hover:text-sage">
                      {s}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </section>

          <HowItWorks t={t} />
          <Faq t={t} />

          <section className="px-5 sm:px-10 lg:px-16 pb-20 sm:pb-24" data-testid="groups-band">
            <Reveal>
              <div className="max-w-4xl mx-auto bg-white border border-line rounded-3xl px-8 sm:px-12 py-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
                <span className="grid place-items-center h-14 w-14 shrink-0 rounded-full bg-sand border border-line">
                  <Users className="h-6 w-6 text-sage" />
                </span>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl tracking-tight mb-1.5">{t.groupsTitle}</h3>
                  <p className="text-sm leading-relaxed text-stonemute">{t.groupsText}</p>
                </div>
                <motion.a href={t.groupsWaUrl} target="_blank" rel="noopener noreferrer" data-testid="groups-cta-button"
                  whileTap={{ scale: 0.97 }}
                  className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border-2 border-sage px-7 py-3.5 text-sm font-bold text-sage transition-colors duration-300 hover:bg-sage hover:text-white">
                  <MessageCircle className="h-4 w-4" /> {t.groupsCta}
                </motion.a>
              </div>
            </Reveal>
          </section>

          <section id="formulario" className="px-5 sm:px-10 lg:px-16 py-20 sm:py-28 scroll-mt-16" data-testid="form-section">
            <div className="max-w-2xl mx-auto">
              <Reveal className="text-center mb-10">
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-sage mb-5">{t.formKicker}</p>
                <h2 className="font-serif tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  {t.formTitle} <em className="text-sage">{t.formAccent}</em>
                </h2>
                <p className="text-base leading-relaxed text-stonemute max-w-md mx-auto">
                  {t.formSub}
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                {leadName ? <SuccessCard name={leadName} t={t.sc} /> : <LeadForm onSuccess={setLeadName} t={t.lf} />}
              </Reveal>
            </div>
          </section>
        </main>
        <SiteFooter t={t.footer} />
        <motion.a
          href={t.waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="floating-whatsapp-button"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] pl-4 pr-6 py-3.5 text-sm font-bold text-white shadow-[0_12px_30px_-8px_rgba(37,211,102,0.6)] transition-colors duration-300 hover:bg-[#1eb85a]"
        >
          <MessageCircle className="h-5 w-5" /> {t.floatingWhats}
        </motion.a>
      </div>
    </ReactLenis>
  );
}
